import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Cell, ComposedChart, Area
} from 'recharts';
import {
  FileText, Download, Users, User, Calendar, TrendingUp, Activity, Scale,
  AlertTriangle, CheckCircle, Clock, Settings, Zap, Target, Filter, Printer,
  ChevronDown, ChevronUp, BarChart3, Heart, Shield
} from 'lucide-react';
import { AdherenceReport, AvailabilityReport, RTPInjuryReport } from '../components/V1Reports';
import { VBTTestingReport } from '../components/VBTReport';

// ─── Constants ────────────────────────────────────────────────
const PERIOD_LABELS = {
  WEEKLY: 'Weekly', MONTHLY: 'Monthly', QUARTERLY: 'Quarterly',
  SEMI_ANNUAL: 'Semi-Annual', SEASONAL: 'Seasonal', CUSTOM: 'Custom'
};

const SECTION_ICONS = {
  load_summary: Zap, gps_metrics: Activity, training_load_trend: TrendingUp,
  wellness_summary: Heart, body_weight: Scale, rtp_summary: Shield,
  injury_history: AlertTriangle, attendance: CheckCircle, recovery_sessions: Clock,
  match_vs_training: BarChart3, previous_period_comparison: TrendingUp,
  team_summary: Users, load_rankings: BarChart3, gps_averages: Activity,
  squad_availability: CheckCircle, injury_report: AlertTriangle, rtp_alerts: Shield,
  wellness_overview: Heart, attendance_summary: CheckCircle, missing_rpe: Clock,
  match_analysis: Target, training_vs_match: BarChart3
};

const SECTION_LABELS = {
  load_summary: 'Load Summary', gps_metrics: 'GPS Metrics',
  training_load_trend: 'Training Load Trend', wellness_summary: 'Wellness Summary',
  body_weight: 'Body Weight', rtp_summary: 'RTP Summary',
  injury_history: 'Injury History', attendance: 'Attendance',
  recovery_sessions: 'Recovery Sessions', match_vs_training: 'Match vs Training',
  previous_period_comparison: 'Period Comparison', team_summary: 'Team Summary',
  load_rankings: 'Load Rankings', gps_averages: 'GPS Averages',
  squad_availability: 'Squad Availability', injury_report: 'Injury Report',
  rtp_alerts: 'RTP Alerts', wellness_overview: 'Wellness Overview',
  attendance_summary: 'Attendance Summary', missing_rpe: 'Missing RPE',
  match_analysis: 'Match Analysis', training_vs_match: 'Training vs Match'
};

const EVENT_COLORS = {
  TRAINING: '#f59e0b', MATCH: '#3b82f6', FRIENDLY_MATCH: '#8b5cf6',
  RTP: '#ef4444', RECOVERY: '#22c55e', default: '#71717a'
};

const CHART_THEME = {
  bg: 'transparent', grid: '#27272a', text: '#a1a1aa', accent: '#f59e0b',
  blue: '#3b82f6', green: '#22c55e', red: '#ef4444', purple: '#8b5cf6'
};

// ─── Chart Tooltip ────────────────────────────────────────────
const ChartTooltipContent = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-xs shadow-lg">
      <p className="text-zinc-400 mb-1 font-medium">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-mono">
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

// ─── Small Stat Card ──────────────────────────────────────────
const Stat = ({ label, value, sub, highlight, warning }) => (
  <div className="stat-card p-3 bg-zinc-950/50 border border-zinc-800 rounded-sm text-center" data-testid={`stat-${label?.toLowerCase().replace(/\s/g,'-')}`}>
    <div className={`text-xl font-bold font-mono ${highlight ? 'text-amber-400' : warning ? 'text-rose-400' : 'text-zinc-100'} stat-value`}>
      {value ?? '-'}
    </div>
    <div className="text-xs text-zinc-500 mt-0.5 stat-label">{label}</div>
    {sub && <div className="text-[10px] text-zinc-600 mt-0.5">{sub}</div>}
  </div>
);

// ─── Section Wrapper ──────────────────────────────────────────
const Section = ({ title, icon: Icon, children, id }) => (
  <div className="report-section mb-6 break-inside-avoid" data-testid={`report-section-${id}`}>
    <Card className="bg-zinc-900/50 border-zinc-800 print:bg-white print:border-gray-200 print:shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-zinc-100 flex items-center gap-2 print:text-gray-900 section-title">
          {Icon && <Icon className="w-4 h-4 text-amber-500 print:text-amber-600" />}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="print:text-gray-700">{children}</CardContent>
    </Card>
  </div>
);

// ─── Comparison Badge ─────────────────────────────────────────
const Delta = ({ current, previous, label, suffix = '' }) => {
  if (!previous || previous === 0) return null;
  const diff = current - previous;
  const pct = ((diff / previous) * 100).toFixed(1);
  const up = diff > 0;
  return (
    <div className="mt-3 flex items-center justify-between p-2 bg-zinc-950/30 border border-zinc-800/50 rounded-sm text-xs print:bg-gray-50 print:border-gray-200">
      <span className="text-zinc-500 print:text-gray-500">{label}</span>
      <span className="flex items-center gap-2">
        <span className="text-zinc-500 font-mono">{Math.round(previous)}{suffix}</span>
        <span className="text-zinc-600">&#8594;</span>
        <span className="text-zinc-100 font-mono font-bold print:text-gray-900">{Math.round(current)}{suffix}</span>
        <span className={`font-mono ${up ? 'text-emerald-400' : 'text-rose-400'}`}>
          ({up ? '+' : ''}{pct}%)
        </span>
      </span>
    </div>
  );
};

// ─── Empty Section ────────────────────────────────────────────
const Empty = ({ text }) => (
  <div className="text-center text-zinc-600 text-sm py-6 print:text-gray-400">{text || 'No data available for this period'}</div>
);

// ═══════════════════════════════════════════════════════════════
//  CHART COMPONENTS
// ═══════════════════════════════════════════════════════════════

const DailyLoadChart = ({ data, title }) => {
  if (!data?.length) return <Empty text="No load data" />;
  const formatted = data.map(d => ({
    ...d,
    date: d.date?.slice(5),
    load: d.training_load || d.total_load || 0
  }));
  return (
    <div className="mt-3" data-testid="daily-load-chart">
      {title && <div className="text-xs text-zinc-500 mb-2 print:text-gray-500">{title}</div>}
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={formatted}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} />
          <XAxis dataKey="date" tick={{ fill: CHART_THEME.text, fontSize: 10 }} />
          <YAxis tick={{ fill: CHART_THEME.text, fontSize: 10 }} />
          <Tooltip content={<ChartTooltipContent />} />
          <Bar dataKey="load" name="Load" radius={[2, 2, 0, 0]}>
            {formatted.map((entry, i) => (
              <Cell key={i} fill={EVENT_COLORS[entry.event_type] || EVENT_COLORS.default} />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
      <div className="flex gap-3 mt-1 justify-center text-[10px]">
        {Object.entries(EVENT_COLORS).filter(([k]) => k !== 'default').map(([k, c]) => (
          <span key={k} className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: c }} />{k.replace('_', ' ')}</span>
        ))}
      </div>
    </div>
  );
};

const WellnessTrendChart = ({ data }) => {
  if (!data?.length) return <Empty text="No wellness data" />;
  const formatted = data.map(d => ({ ...d, date: d.date?.slice(5), readiness: d.readiness || 0 }));
  return (
    <div className="mt-3" data-testid="wellness-trend-chart">
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={formatted}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} />
          <XAxis dataKey="date" tick={{ fill: CHART_THEME.text, fontSize: 10 }} />
          <YAxis domain={[0, 100]} tick={{ fill: CHART_THEME.text, fontSize: 10 }} />
          <Tooltip content={<ChartTooltipContent />} />
          <defs>
            <linearGradient id="wellnessGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={CHART_THEME.green} stopOpacity={0.3} />
              <stop offset="95%" stopColor={CHART_THEME.green} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="readiness" stroke={CHART_THEME.green} fill="url(#wellnessGrad)" name="Readiness" />
          <Line type="monotone" dataKey="readiness" stroke={CHART_THEME.green} strokeWidth={2} dot={{ r: 3 }} name="Readiness" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

const WeightTrendChart = ({ data }) => {
  if (!data?.length) return <Empty text="No weight data" />;
  const formatted = data.map(d => ({ ...d, date: d.date?.slice(5) }));
  const weights = data.map(d => d.weight);
  const minW = Math.floor(Math.min(...weights) - 1);
  const maxW = Math.ceil(Math.max(...weights) + 1);
  return (
    <div className="mt-3" data-testid="weight-trend-chart">
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={formatted}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} />
          <XAxis dataKey="date" tick={{ fill: CHART_THEME.text, fontSize: 10 }} />
          <YAxis domain={[minW, maxW]} tick={{ fill: CHART_THEME.text, fontSize: 10 }} />
          <Tooltip content={<ChartTooltipContent />} />
          <Line type="monotone" dataKey="weight" stroke={CHART_THEME.purple} strokeWidth={2} dot={{ r: 3, fill: CHART_THEME.purple }} name="Weight (kg)" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const TrainingMatchChart = ({ data }) => {
  if (!data) return <Empty />;
  const chartData = [
    { name: 'Training', sessions: data.training?.total_events || 0, load: Math.round(data.training?.total_team_load || data.training?.total_training_load || 0) },
    { name: 'Match', sessions: data.match?.total_events || 0, load: Math.round(data.match?.total_team_load || data.match?.total_training_load || 0) },
    { name: 'Friendly', sessions: data.friendly?.total_events || 0, load: Math.round(data.friendly?.total_team_load || data.friendly?.total_training_load || 0) }
  ];
  return (
    <div className="mt-3" data-testid="training-match-chart">
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} />
          <XAxis dataKey="name" tick={{ fill: CHART_THEME.text, fontSize: 11 }} />
          <YAxis yAxisId="left" tick={{ fill: CHART_THEME.text, fontSize: 10 }} />
          <YAxis yAxisId="right" orientation="right" tick={{ fill: CHART_THEME.text, fontSize: 10 }} />
          <Tooltip content={<ChartTooltipContent />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="left" dataKey="sessions" name="Sessions" fill={CHART_THEME.accent} radius={[2, 2, 0, 0]} />
          <Bar yAxisId="right" dataKey="load" name="Total Load" fill={CHART_THEME.blue} radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const RankingsChart = ({ data }) => {
  const athletes = data?.by_total_load?.highest;
  if (!athletes?.length) return <Empty text="No ranking data" />;
  const chartData = athletes.map(a => ({
    name: a.athlete_name?.split(' ').pop() || 'N/A',
    load: Math.round(a.total_load || 0)
  }));
  return (
    <div className="mt-3" data-testid="rankings-chart">
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={chartData} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} />
          <XAxis type="number" tick={{ fill: CHART_THEME.text, fontSize: 10 }} />
          <YAxis type="category" dataKey="name" width={80} tick={{ fill: CHART_THEME.text, fontSize: 11 }} />
          <Tooltip content={<ChartTooltipContent />} />
          <Bar dataKey="load" name="Total Load" fill={CHART_THEME.accent} radius={[0, 3, 3, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const TeamDailyLoadChart = ({ data }) => {
  if (!data?.length) return <Empty text="No daily load data" />;
  const formatted = data.map(d => ({
    ...d,
    date: d.date?.slice(5),
    avg_load: d.athlete_count > 0 ? Math.round(d.total_load / d.athlete_count) : 0
  }));
  return (
    <div className="mt-3" data-testid="team-daily-load-chart">
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={formatted}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} />
          <XAxis dataKey="date" tick={{ fill: CHART_THEME.text, fontSize: 10 }} />
          <YAxis tick={{ fill: CHART_THEME.text, fontSize: 10 }} />
          <Tooltip content={<ChartTooltipContent />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="total_load" name="Team Load" fill={CHART_THEME.accent} radius={[2, 2, 0, 0]}>
            {formatted.map((entry, i) => (
              <Cell key={i} fill={EVENT_COLORS[entry.event_type] || EVENT_COLORS.default} />
            ))}
          </Bar>
          <Line type="monotone" dataKey="avg_load" name="Avg/Athlete" stroke={CHART_THEME.blue} strokeWidth={2} dot={{ r: 2 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  REPORT SECTION RENDERERS
// ═══════════════════════════════════════════════════════════════

// ─── Team: Load / Summary ─────────────────────────────────────
const TeamLoadSection = ({ load, loadPrev, comparison, charts, lang }) => {
  if (!load) return null;
  return (
    <Section title={lang === 'IT' ? 'Riepilogo Carico' : 'Load Summary'} icon={Zap} id="team-load">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label={lang === 'IT' ? 'Sessioni' : 'Sessions'} value={load.total_events} />
        <Stat label={lang === 'IT' ? 'Carico Totale' : 'Total Load'} value={Math.round(load.total_team_load)} highlight />
        <Stat label={lang === 'IT' ? 'Media/Atleta' : 'Avg/Athlete'} value={Math.round(load.avg_load_per_athlete)} />
        <Stat label="RPE" value={load.avg_rpe ?? '-'} />
      </div>
      {load.total_distance > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-3">
          <Stat label="Distance (m)" value={load.total_distance?.toLocaleString()} />
          <Stat label="SHI Dist" value={load.avg_shi?.toLocaleString()} />
          <Stat label="Sprint Dist" value={load.avg_s7?.toLocaleString()} />
          <Stat label={lang === 'IT' ? 'Durata Media' : 'Avg Duration'} value={`${load.avg_session_duration}'`} />
          <Stat label="RPE %" value={`${load.rpe_compliance}%`} highlight={load.rpe_compliance >= 90} warning={load.rpe_compliance < 70} />
        </div>
      )}
      {comparison && loadPrev && (
        <Delta current={load.total_team_load} previous={loadPrev.total_team_load} label={lang === 'IT' ? 'vs periodo prec.' : 'vs previous'} />
      )}
      {charts && <TeamDailyLoadChart data={load.daily_team_loads} />}
    </Section>
  );
};

// ─── Team: Training vs Match ──────────────────────────────────
const TeamTrainingMatchSection = ({ data, charts, lang }) => {
  if (!data) return null;
  return (
    <Section title={lang === 'IT' ? 'Allenamento vs Partita' : 'Training vs Match'} icon={BarChart3} id="training-match">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { key: 'training', label: lang === 'IT' ? 'Allenamento' : 'Training', color: 'text-amber-400' },
          { key: 'match', label: lang === 'IT' ? 'Partite' : 'Matches', color: 'text-blue-400' },
          { key: 'friendly', label: lang === 'IT' ? 'Amichevoli' : 'Friendlies', color: 'text-purple-400' }
        ].map(({ key, label, color }) => (
          <div key={key} className="p-3 bg-zinc-950/50 border border-zinc-800 rounded-sm print:bg-gray-50 print:border-gray-200">
            <div className="text-xs text-zinc-500 mb-1 print:text-gray-500">{label}</div>
            <div className={`text-2xl font-bold ${color}`}>{data[key]?.total_events || 0}</div>
            <div className="text-xs text-zinc-400 print:text-gray-600">
              {Math.round(data[key]?.total_team_load || data[key]?.total_training_load || 0)} load
            </div>
          </div>
        ))}
      </div>
      {charts && <TrainingMatchChart data={data} />}
    </Section>
  );
};

// ─── Team: Match Analysis ─────────────────────────────────────
const TeamMatchAnalysis = ({ data, lang }) => {
  if (!data) return null;
  return (
    <Section title={lang === 'IT' ? 'Analisi Partite' : 'Match Analysis'} icon={Target} id="match-analysis">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { key: 'all_players', label: lang === 'IT' ? 'Tutti i Giocatori' : 'All Players', accent: false },
          { key: 'sixty_plus_minutes', label: lang === 'IT' ? 'Solo 60+ Min' : '60+ Minutes Only', accent: true }
        ].map(({ key, label, accent }) => (
          <div key={key} className={`p-3 rounded-sm border ${accent ? 'bg-amber-500/10 border-amber-500/30' : 'bg-zinc-950/50 border-zinc-800'} print:bg-gray-50 print:border-gray-200`}>
            <div className={`text-xs mb-2 ${accent ? 'text-amber-500' : 'text-zinc-500'} print:text-gray-600`}>{label}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div><span className="text-zinc-500">Athletes:</span> <span className="text-zinc-100 print:text-gray-900">{data[key]?.unique_athletes || 0}</span></div>
              <div><span className="text-zinc-500">Avg Load:</span> <span className={`font-bold ${accent ? 'text-amber-400' : 'text-zinc-100'} print:text-gray-900`}>{Math.round(data[key]?.avg_load_per_session || 0)}</span></div>
              <div><span className="text-zinc-500">Avg Dist:</span> <span className="text-zinc-100 print:text-gray-900">{data[key]?.avg_distance?.toLocaleString() || 0}m</span></div>
              <div><span className="text-zinc-500">Avg RPE:</span> <span className="text-zinc-100 print:text-gray-900">{data[key]?.avg_rpe || '-'}</span></div>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

// ─── Team: Availability ───────────────────────────────────────
const TeamAvailability = ({ data, lang }) => {
  if (!data) return null;
  return (
    <Section title={lang === 'IT' ? 'Disponibilita Squadra' : 'Squad Availability'} icon={CheckCircle} id="availability">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label={lang === 'IT' ? 'Tasso Presenza' : 'Attendance'} value={`${data.attendance_rate}%`} highlight={data.attendance_rate >= 90} warning={data.attendance_rate < 80} />
        <Stat label={lang === 'IT' ? 'Sessioni Perse' : 'Missed'} value={data.total_possible_sessions - data.total_attended_sessions} warning />
        <Stat label={lang === 'IT' ? 'RTP Attivi' : 'Active RTP'} value={data.active_rtp_cases} warning={data.active_rtp_cases > 0} />
        <Stat label={lang === 'IT' ? 'RPE Mancanti' : 'Missing RPE'} value={data.athletes_with_missing_rpe} warning={data.athletes_with_missing_rpe > 0} />
      </div>
    </Section>
  );
};

// ─── Team: Injury Report ──────────────────────────────────────
const TeamInjurySection = ({ data, lang }) => {
  if (!data) return null;
  return (
    <Section title={lang === 'IT' ? 'Report Infortuni' : 'Injury Report'} icon={AlertTriangle} id="injury">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label={lang === 'IT' ? 'Nuovi' : 'New'} value={data.new_injuries_in_period} warning={data.new_injuries_in_period > 0} />
        <Stat label={lang === 'IT' ? 'Attivi' : 'Active'} value={data.total_active_injuries} warning={data.total_active_injuries > 0} />
        <Stat label={lang === 'IT' ? 'Giorni Persi' : 'Days Lost'} value={data.total_days_lost} warning />
        <Stat label={lang === 'IT' ? 'Rientrati' : 'Cleared'} value={data.cleared_in_period} highlight />
      </div>
      {data.currently_in_rtp?.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-medium text-zinc-400 mb-2 print:text-gray-600">
            {lang === 'IT' ? 'Attualmente in RTP:' : 'Currently in RTP:'}
          </div>
          <div className="space-y-1.5">
            {data.currently_in_rtp.map((a, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-zinc-950/50 border border-zinc-800 rounded-sm text-sm print:bg-gray-50 print:border-gray-200">
                <span className="text-zinc-100 font-medium print:text-gray-900">{a.athlete_name}</span>
                <span className="text-zinc-500 text-xs print:text-gray-500">{a.injury_type} &middot; {a.current_phase}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
};

// ─── Team: Rankings ───────────────────────────────────────────
const TeamRankingsSection = ({ data, charts, tables, lang }) => {
  if (!data) return null;
  return (
    <Section title={lang === 'IT' ? 'Classifiche Carico' : 'Load Rankings'} icon={BarChart3} id="rankings">
      {charts && <RankingsChart data={data} />}
      {tables && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <div>
            <div className="text-xs font-medium text-zinc-400 mb-2 print:text-gray-600">{lang === 'IT' ? 'Carico piu alto' : 'Highest Load'}</div>
            <div className="space-y-1">
              {data.by_total_load?.highest?.map((a, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-zinc-950/50 border border-zinc-800 rounded-sm text-sm print:bg-gray-50 print:border-gray-200">
                  <span className="text-zinc-100 print:text-gray-900"><span className="text-amber-500 font-bold mr-1">#{i + 1}</span>{a.athlete_name}</span>
                  <span className="text-amber-400 font-mono text-xs">{Math.round(a.total_load)}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-zinc-400 mb-2 print:text-gray-600">{lang === 'IT' ? 'Carico piu basso' : 'Lowest Load'}</div>
            <div className="space-y-1">
              {data.by_total_load?.lowest?.map((a, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-zinc-950/50 border border-zinc-800 rounded-sm text-sm print:bg-gray-50 print:border-gray-200">
                  <span className="text-zinc-100 print:text-gray-900">{a.athlete_name}</span>
                  <span className="text-zinc-400 font-mono text-xs">{Math.round(a.total_load)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {data.by_missing_rpe?.length > 0 && tables && (
        <div className="mt-4">
          <div className="text-xs font-medium text-amber-400 mb-2">{lang === 'IT' ? 'RPE Mancanti' : 'Missing RPE'}</div>
          <div className="space-y-1">
            {data.by_missing_rpe.map((a, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-amber-500/10 border border-amber-500/30 rounded-sm text-sm">
                <span className="text-zinc-100">{a.athlete_name}</span>
                <span className="text-amber-400 font-mono text-xs">{a.rpe_missing} missing</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
};

// ─── Athlete: Load ────────────────────────────────────────────
const AthleteLoadSection = ({ load, loadPrev, trend, comparison, charts, lang }) => {
  if (!load) return null;
  return (
    <Section title={lang === 'IT' ? 'Carico / Performance' : 'Load / Performance'} icon={Zap} id="athlete-load">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label={lang === 'IT' ? 'Sessioni' : 'Sessions'} value={load.total_sessions} />
        <Stat label={lang === 'IT' ? 'Carico Tot.' : 'Total Load'} value={Math.round(load.total_training_load)} highlight />
        <Stat label={lang === 'IT' ? 'Media' : 'Avg Load'} value={Math.round(load.avg_training_load)} />
        <Stat label="RPE" value={load.avg_rpe ?? '-'} />
      </div>
      {load.total_distance > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mt-3">
          <Stat label="Distance" value={`${load.total_distance?.toLocaleString()}m`} />
          <Stat label="D/min" value={`${load.avg_distance_per_min || 0}`} />
          <Stat label="SHI" value={`${load.total_shi_distance?.toLocaleString() || 0}m`} />
          <Stat label="Sprint" value={`${load.total_s7_distance?.toLocaleString() || 0}m`} />
          <Stat label="AccHI" value={`${load.total_acchi_distance?.toLocaleString() || 0}m`} />
          <Stat label="DecHI" value={`${load.total_dechi_distance?.toLocaleString() || 0}m`} />
          <Stat label="SMax" value={`${load.speed_max || 0} km/h`} />
          <Stat label={lang === 'IT' ? 'Minuti' : 'Minutes'} value={load.total_minutes} />
        </div>
      )}
      {comparison && loadPrev && (
        <Delta current={load.total_training_load} previous={loadPrev.total_training_load} label={lang === 'IT' ? 'vs periodo prec.' : 'vs previous'} />
      )}
      {charts && <DailyLoadChart data={trend || load.daily_loads} title={lang === 'IT' ? 'Andamento Carico Giornaliero' : 'Daily Training Load Trend'} />}
    </Section>
  );
};

// ─── Athlete: Wellness ────────────────────────────────────────
const AthleteWellnessSection = ({ data, dataPrev, comparison, charts, lang }) => {
  if (!data) return null;
  return (
    <Section title="Wellness" icon={Heart} id="athlete-wellness">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat label={lang === 'IT' ? 'Readiness' : 'Readiness'} value={data.avg_readiness ?? '-'} highlight />
        <Stat label={lang === 'IT' ? 'Sonno' : 'Sleep'} value={data.avg_sleep ?? '-'} />
        <Stat label={lang === 'IT' ? 'Stress' : 'Stress'} value={data.avg_stress ?? '-'} />
        <Stat label={lang === 'IT' ? 'Fatica' : 'Fatigue'} value={data.avg_fatigue ?? '-'} />
        <Stat label="Submissions" value={data.total_submissions} />
      </div>
      {comparison && dataPrev && (
        <Delta current={data.avg_readiness} previous={dataPrev.avg_readiness} label={lang === 'IT' ? 'Readiness vs prec.' : 'Readiness vs prev'} />
      )}
      {charts && <WellnessTrendChart data={data.daily_wellness} />}
    </Section>
  );
};

// ─── Athlete: Weight ──────────────────────────────────────────
const AthleteWeightSection = ({ data, charts, lang }) => {
  if (!data || data.total_entries === 0) return null;
  return (
    <Section title={lang === 'IT' ? 'Peso Corporeo' : 'Body Weight'} icon={Scale} id="athlete-weight">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat label={lang === 'IT' ? 'Inizio' : 'Start'} value={`${data.start_weight} kg`} />
        <Stat label={lang === 'IT' ? 'Fine' : 'End'} value={`${data.end_weight} kg`} highlight />
        <Stat label={lang === 'IT' ? 'Variazione' : 'Change'} value={`${data.weight_change > 0 ? '+' : ''}${data.weight_change} kg`} warning={Math.abs(data.weight_change) > 2} />
        <Stat label="Min" value={`${data.min_weight} kg`} />
        <Stat label="Max" value={`${data.max_weight} kg`} />
      </div>
      {charts && <WeightTrendChart data={data.daily_weights} />}
    </Section>
  );
};

// ─── Athlete: RTP ─────────────────────────────────────────────
const AthleteRTPSection = ({ data, lang }) => {
  if (!data) return null;
  return (
    <Section title="RTP / Injuries" icon={Shield} id="athlete-rtp">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label={lang === 'IT' ? 'Infortuni' : 'Injuries'} value={data.total_injuries_in_period} warning={data.total_injuries_in_period > 0} />
        <Stat label={lang === 'IT' ? 'Giorni RTP' : 'RTP Days'} value={data.rtp_days} warning={data.rtp_days > 0} />
        <Stat label={lang === 'IT' ? 'Sessioni RTP' : 'RTP Sessions'} value={data.rtp_sessions_completed} />
        <Stat label={lang === 'IT' ? 'Rientrato' : 'Cleared'} value={data.cleared_in_period} highlight={data.cleared_in_period > 0} />
      </div>
      {data.injuries?.length > 0 && (
        <div className="mt-4 space-y-1.5">
          <div className="text-xs text-zinc-400 font-medium print:text-gray-600">{lang === 'IT' ? 'Dettagli Infortuni' : 'Injury Details'}</div>
          {data.injuries.map((inj, i) => (
            <div key={i} className="flex items-center justify-between p-2 bg-zinc-950/50 border border-zinc-800 rounded-sm text-sm print:bg-gray-50 print:border-gray-200">
              <span className="text-zinc-100 print:text-gray-900">{inj.injury_type}</span>
              <span className="text-xs text-zinc-500 print:text-gray-500">{inj.injury_date} &middot; {inj.status} &middot; {inj.current_phase}</span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

// ─── Athlete: Attendance ──────────────────────────────────────
const AthleteAttendanceSection = ({ data, lang }) => {
  if (!data) return null;
  return (
    <Section title={lang === 'IT' ? 'Partecipazione' : 'Attendance'} icon={CheckCircle} id="athlete-attendance">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat label={lang === 'IT' ? 'Presenze' : 'Attended'} value={data.events_attended} />
        <Stat label={lang === 'IT' ? 'Assenze' : 'Missed'} value={data.events_missed} warning={data.events_missed > 0} />
        <Stat label={lang === 'IT' ? 'Tasso' : 'Rate'} value={`${data.attendance_rate}%`} highlight={data.attendance_rate >= 90} warning={data.attendance_rate < 80} />
        <Stat label={lang === 'IT' ? 'Allenamenti' : 'Trainings'} value={data.training_sessions} />
        <Stat label="Recovery" value={data.recovery_sessions} />
      </div>
      {data.missed_events?.length > 0 && (
        <div className="mt-4">
          <div className="text-xs text-zinc-400 font-medium mb-2 print:text-gray-600">{lang === 'IT' ? 'Sessioni Mancate' : 'Missed Sessions'}</div>
          <div className="space-y-1">
            {data.missed_events.map((e, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-zinc-950/50 border border-zinc-800 rounded-sm text-xs print:bg-gray-50 print:border-gray-200">
                <span className="text-zinc-100 print:text-gray-900">{e.title || e.event_type}</span>
                <span className="text-zinc-500 print:text-gray-500">{e.date} &middot; {e.event_type}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
};

// ═══════════════════════════════════════════════════════════════
//  MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════

const StaffReportsPage = () => {
  const { api } = useAuth();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [teams, setTeams] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [activeTab, setActiveTab] = useState('adherence');

  // V1 Report filters (shared)
  const [v1TeamId, setV1TeamId] = useState('');
  const [v1AthleteId, setV1AthleteId] = useState('');
  const [v1DateFrom, setV1DateFrom] = useState('');
  const [v1DateTo, setV1DateTo] = useState('');
  const [v1Athletes, setV1Athletes] = useState([]);

  // Builder state (advanced)
  const [reportType, setReportType] = useState('TEAM');
  const [periodType, setPeriodType] = useState('WEEKLY');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedSections, setSelectedSections] = useState([]);
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeTables, setIncludeTables] = useState(true);
  const [includeComparison, setIncludeComparison] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState(null);
  const [minMinutesFilter, setMinMinutesFilter] = useState(null);

  // Report data
  const [report, setReport] = useState(null);
  const [periodDates, setPeriodDates] = useState(null);

  const reportRef = useRef(null);

  // Set default V1 date range (last 30 days)
  useEffect(() => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - 30);
    setV1DateTo(today.toISOString().split('T')[0]);
    setV1DateFrom(from.toISOString().split('T')[0]);
  }, []);

  // Fetch V1 athletes when team changes
  useEffect(() => {
    if (v1TeamId) {
      api.get(`/athletes?team_id=${v1TeamId}`).then(r => setV1Athletes(r.data)).catch(() => {});
    } else {
      setV1Athletes([]);
    }
    setV1AthleteId('');
  }, [api, v1TeamId]);

  // ─── Fetch config + teams on mount ──────────────────────────
  const fetchConfig = useCallback(async () => {
    try {
      const [configRes, teamsRes] = await Promise.all([
        api.get('/reports/config'), api.get('/teams')
      ]);
      setConfig(configRes.data);
      setTeams(teamsRes.data);
      setSelectedSections(configRes.data.team_sections.slice(0, 6));
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  }, [api]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  // ─── Fetch athletes for athlete reports ─────────────────────
  useEffect(() => {
    if (reportType === 'ATHLETE' && selectedTeam) {
      api.get(`/athletes?team_id=${selectedTeam}`).then(r => setAthletes(r.data)).catch(() => {});
    }
  }, [api, reportType, selectedTeam]);

  // ─── Auto-calculate period dates ────────────────────────────
  useEffect(() => {
    if (periodType && periodType !== 'CUSTOM') {
      api.get(`/reports/periods?period_type=${periodType}`).then(r => {
        setPeriodDates(r.data);
        setDateFrom(r.data.current.from);
        setDateTo(r.data.current.to);
      }).catch(() => {});
    }
  }, [api, periodType]);

  // ─── Update sections when type changes ──────────────────────
  useEffect(() => {
    if (config) {
      const s = reportType === 'ATHLETE' ? config.athlete_sections : config.team_sections;
      setSelectedSections(s.slice(0, 6));
    }
  }, [reportType, config]);

  // ─── Generate Report ────────────────────────────────────────
  const handleGenerate = async () => {
    if (reportType === 'TEAM' && !selectedTeam) return toast.error('Select a team');
    if (reportType === 'ATHLETE' && !selectedAthlete) return toast.error('Select an athlete');

    setLoading(true);
    try {
      const endpoint = reportType === 'ATHLETE' ? '/reports/athlete' : '/reports/team';
      const payload = {
        report_type: reportType,
        period_type: periodType,
        date_from: dateFrom, date_to: dateTo,
        team_id: selectedTeam || undefined,
        athlete_id: selectedAthlete || undefined,
        sections: selectedSections,
        include_charts: includeCharts,
        include_tables: includeTables,
        include_comparison: includeComparison,
        event_type_filter: eventTypeFilter || undefined,
        min_minutes_filter: minMinutesFilter ? parseInt(minMinutesFilter) : undefined
      };
      const res = await api.post(endpoint, payload);
      setReport(res.data);
      setActiveTab('view');
      toast.success(language === 'IT' ? 'Report generato!' : 'Report generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  // ─── PDF Export via print ───────────────────────────────────
  const handleExportPDF = () => {
    if (!reportRef.current) return;
    const original = document.body.innerHTML;
    const reportName = report?.team?.name || report?.athlete?.name || 'Report';
    const periodLabel = PERIOD_LABELS[report?.period?.type] || '';
    document.title = `${reportName} - ${periodLabel} Report`;

    const printHTML = `
      <html>
        <head>
          <title>${reportName} - ${periodLabel} Report</title>
          <style>
            @page { size: A4; margin: 18mm 14mm; }
            *, *::before, *::after { box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1a1a1a; background: #fff; margin: 0; padding: 0; line-height: 1.5; font-size: 12px; }
            .print-header { border-bottom: 3px solid #d97706; padding-bottom: 12px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: flex-end; }
            .print-header h1 { font-size: 22px; font-weight: 700; margin: 0; color: #111; }
            .print-header .sub { font-size: 11px; color: #666; margin-top: 2px; }
            .print-header .meta { text-align: right; font-size: 10px; color: #888; }
            .report-section { margin-bottom: 20px; page-break-inside: avoid; }
            .section-title { font-size: 14px; font-weight: 600; color: #333; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px; margin-bottom: 10px; }
            .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 8px; }
            .stat-grid-5 { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin-bottom: 8px; }
            .stat-card { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px; text-align: center; }
            .stat-value { font-size: 18px; font-weight: 700; color: #111; }
            .stat-value.highlight { color: #d97706; }
            .stat-value.warning { color: #dc2626; }
            .stat-label { font-size: 10px; color: #666; margin-top: 2px; }
            .delta-row { display: flex; justify-content: space-between; align-items: center; padding: 6px 10px; background: #f3f4f6; border-radius: 4px; font-size: 11px; margin-top: 6px; }
            .list-item { display: flex; justify-content: space-between; align-items: center; padding: 6px 10px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; margin-bottom: 4px; font-size: 11px; }
            .rank-badge { color: #d97706; font-weight: 700; margin-right: 4px; }
            .chart-container { margin: 10px 0; text-align: center; }
            .chart-container svg { max-width: 100%; }
            .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
            .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
            .type-card { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px; }
            .type-card .type-label { font-size: 10px; color: #666; margin-bottom: 2px; }
            .type-card .type-value { font-size: 20px; font-weight: 700; }
            .type-card .type-sub { font-size: 10px; color: #888; }
            .footer { text-align: right; font-size: 9px; color: #aaa; margin-top: 24px; border-top: 1px solid #eee; padding-top: 6px; }
            .amber { color: #d97706; }
            .blue { color: #2563eb; }
            .purple { color: #7c3aed; }
            .green { color: #16a34a; }
            .red { color: #dc2626; }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div>
              <h1>${reportName}</h1>
              <div class="sub">${periodLabel}: ${report?.period?.from || ''} &rarr; ${report?.period?.to || ''}</div>
            </div>
            <div class="meta">
              FB Performance System<br/>
              Generated: ${new Date(report?.generated_at).toLocaleDateString()}<br/>
              ${report?.report_type === 'ATHLETE' ? `Position: ${report?.athlete?.position || 'N/A'} | Status: ${report?.athlete?.status || 'N/A'}` : `Team Code: ${report?.team?.code || ''}`}
            </div>
          </div>
          ${reportRef.current.innerHTML}
          <div class="footer">
            This report was generated by FB Performance System on ${new Date().toLocaleString()}
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printHTML);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // ─── Section toggles ───────────────────────────────────────
  const toggleSection = (s) => setSelectedSections(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const selectAll = () => setSelectedSections(reportType === 'ATHLETE' ? config.athlete_sections : config.team_sections);
  const clearAll = () => setSelectedSections([]);

  // ─── Loading state ──────────────────────────────────────────
  if (!config) {
    return (
      <div className="space-y-6 fade-in" data-testid="staff-reports-page">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-100">{language === 'IT' ? 'Report' : 'Reports'}</h1>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-8 text-center text-zinc-500">Loading configuration...</CardContent>
        </Card>
      </div>
    );
  }

  const availableSections = reportType === 'ATHLETE' ? config.athlete_sections : config.team_sections;

  // ═══════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <div className="space-y-6 fade-in" data-testid="staff-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100">{language === 'IT' ? 'Report' : 'Reports'}</h1>
          <p className="text-sm text-zinc-500 mt-1">{language === 'IT' ? 'Report pratici e analisi avanzate' : 'Practical reports & advanced analytics'}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900/50 border border-zinc-800 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="adherence" className="data-[state=active]:bg-zinc-800 text-xs sm:text-sm" data-testid="tab-adherence">
            <CheckCircle className="w-3.5 h-3.5 mr-1.5 hidden sm:inline" />Adherence
          </TabsTrigger>
          <TabsTrigger value="availability" className="data-[state=active]:bg-zinc-800 text-xs sm:text-sm" data-testid="tab-availability">
            <Heart className="w-3.5 h-3.5 mr-1.5 hidden sm:inline" />Availability
          </TabsTrigger>
          <TabsTrigger value="rtp" className="data-[state=active]:bg-zinc-800 text-xs sm:text-sm" data-testid="tab-rtp">
            <Shield className="w-3.5 h-3.5 mr-1.5 hidden sm:inline" />RTP / Injury
          </TabsTrigger>
          <TabsTrigger
            value="rtp-analytics"
            className="data-[state=active]:bg-zinc-800 text-xs sm:text-sm"
            data-testid="tab-rtp-analytics"
            onClick={(e) => { e.preventDefault(); window.location.href = '/staff/reports/rtp-analytics'; }}
          >
            <BarChart3 className="w-3.5 h-3.5 mr-1.5 hidden sm:inline" />RTP Analytics
          </TabsTrigger>
          <TabsTrigger
            value="training-distribution"
            className="data-[state=active]:bg-zinc-800 text-xs sm:text-sm"
            data-testid="tab-training-distribution"
            onClick={(e) => { e.preventDefault(); window.location.href = '/staff/reports/training-distribution'; }}
          >
            <Activity className="w-3.5 h-3.5 mr-1.5 hidden sm:inline" />Training Distribution
          </TabsTrigger>
          <TabsTrigger value="vbt" className="data-[state=active]:bg-zinc-800 text-xs sm:text-sm" data-testid="tab-vbt">
            <Zap className="w-3.5 h-3.5 mr-1.5 hidden sm:inline" />VBT Testing
          </TabsTrigger>
          <TabsTrigger value="advanced" className="data-[state=active]:bg-zinc-800 text-xs sm:text-sm" data-testid="tab-advanced">
            <Settings className="w-3.5 h-3.5 mr-1.5 hidden sm:inline" />Advanced
          </TabsTrigger>
          {report && (
            <TabsTrigger value="view" className="data-[state=active]:bg-zinc-800 text-xs sm:text-sm" data-testid="tab-view">
              <FileText className="w-3.5 h-3.5 mr-1.5 hidden sm:inline" />Dashboard
            </TabsTrigger>
          )}
        </TabsList>

        {/* ═══ V1 REPORT FILTER BAR (shared across 3 tabs) ═══ */}
        {['adherence', 'availability', 'rtp', 'vbt'].includes(activeTab) && (
          <Card className="bg-zinc-900/50 border-zinc-800 mt-4">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="v1-filters">
                <div>
                  <Label className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1 block">Date From</Label>
                  <Input type="date" value={v1DateFrom} onChange={e => setV1DateFrom(e.target.value)}
                    className="bg-zinc-950 border-zinc-800 h-9 text-sm" data-testid="filter-date-from" />
                </div>
                <div>
                  <Label className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1 block">Date To</Label>
                  <Input type="date" value={v1DateTo} onChange={e => setV1DateTo(e.target.value)}
                    className="bg-zinc-950 border-zinc-800 h-9 text-sm" data-testid="filter-date-to" />
                </div>
                <div>
                  <Label className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1 block">Team</Label>
                  <Select value={v1TeamId || "all"} onValueChange={v => setV1TeamId(v === "all" ? "" : v)}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 h-9 text-sm" data-testid="filter-team">
                      <SelectValue placeholder="All Teams" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="all">All Teams</SelectItem>
                      {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.code} — {t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1 block">Athlete</Label>
                  <Select value={v1AthleteId || "all"} onValueChange={v => setV1AthleteId(v === "all" ? "" : v)} disabled={!v1TeamId}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 h-9 text-sm" data-testid="filter-athlete">
                      <SelectValue placeholder={v1TeamId ? "All Athletes" : "Select team first"} />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="all">All Athletes</SelectItem>
                      {v1Athletes.map(a => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ═══ V1 REPORT TABS ═══ */}
        <TabsContent value="adherence" className="mt-4">
          <AdherenceReport dateFrom={v1DateFrom} dateTo={v1DateTo} teamId={v1TeamId} athleteId={v1AthleteId} />
        </TabsContent>
        <TabsContent value="availability" className="mt-4">
          <AvailabilityReport dateFrom={v1DateFrom} dateTo={v1DateTo} teamId={v1TeamId} athleteId={v1AthleteId} />
        </TabsContent>
        <TabsContent value="rtp" className="mt-4">
          <RTPInjuryReport dateFrom={v1DateFrom} dateTo={v1DateTo} teamId={v1TeamId} athleteId={v1AthleteId} />
        </TabsContent>
        <TabsContent value="vbt" className="mt-4">
          <VBTTestingReport dateFrom={v1DateFrom} dateTo={v1DateTo} teamId={v1TeamId} athleteId={v1AthleteId} />
        </TabsContent>

        {/* ═══ ADVANCED BUILDER TAB ═══ */}
        <TabsContent value="advanced" className="mt-4 space-y-5">
          {/* Builder — existing builder content below */}
          {/* Report Type */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-zinc-100 text-base">{language === 'IT' ? 'Tipo Report' : 'Report Type'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                {[
                  { val: 'TEAM', icon: Users, label: language === 'IT' ? 'Squadra' : 'Team Report' },
                  { val: 'ATHLETE', icon: User, label: language === 'IT' ? 'Atleta' : 'Athlete Report' }
                ].map(({ val, icon: Ic, label }) => (
                  <Button key={val} variant={reportType === val ? 'default' : 'outline'}
                    onClick={() => setReportType(val)}
                    className={reportType === val ? 'bg-amber-500 text-zinc-950 hover:bg-amber-400' : 'border-zinc-700 text-zinc-300'}
                    data-testid={`report-type-${val.toLowerCase()}`}
                  >
                    <Ic className="w-4 h-4 mr-2" />{label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Selection & Period */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Entity */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
                  {reportType === 'TEAM' ? <Users className="w-4 h-4 text-amber-500" /> : <User className="w-4 h-4 text-amber-500" />}
                  {reportType === 'TEAM' ? (language === 'IT' ? 'Squadra' : 'Select Team') : (language === 'IT' ? 'Atleta' : 'Select Athlete')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs">{language === 'IT' ? 'Squadra' : 'Team'}</Label>
                  <Select value={selectedTeam} onValueChange={(v) => { setSelectedTeam(v); setSelectedAthlete(null); }}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="team-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.code} - {t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {reportType === 'ATHLETE' && (
                  <div className="space-y-1.5">
                    <Label className="text-zinc-400 text-xs">{language === 'IT' ? 'Atleta' : 'Athlete'}</Label>
                    <Select value={selectedAthlete} onValueChange={setSelectedAthlete} disabled={!selectedTeam}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="athlete-select">
                        <SelectValue placeholder={selectedTeam ? 'Select athlete...' : 'Select a team first'} />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {athletes.map(a => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Period */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-amber-500" />{language === 'IT' ? 'Periodo' : 'Period'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs">{language === 'IT' ? 'Tipo' : 'Type'}</Label>
                  <Select value={periodType} onValueChange={setPeriodType}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="period-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {config.periods.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-zinc-400 text-xs">{language === 'IT' ? 'Da' : 'From'}</Label>
                    <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-zinc-950 border-zinc-800" disabled={periodType !== 'CUSTOM'} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-zinc-400 text-xs">{language === 'IT' ? 'A' : 'To'}</Label>
                    <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-zinc-950 border-zinc-800" disabled={periodType !== 'CUSTOM'} />
                  </div>
                </div>
                {periodDates && <div className="text-[10px] text-zinc-600">{language === 'IT' ? 'Prec: ' : 'Prev: '}{periodDates.previous.from} &rarr; {periodDates.previous.to}</div>}
              </CardContent>
            </Card>
          </div>

          {/* Sections */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
                  <Target className="w-4 h-4 text-amber-500" />{language === 'IT' ? 'Sezioni' : 'Sections'}
                  <span className="text-xs text-zinc-500 font-normal ml-1">({selectedSections.length}/{availableSections.length})</span>
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll} className="border-zinc-700 text-[10px] h-7">{language === 'IT' ? 'Tutte' : 'All'}</Button>
                  <Button variant="outline" size="sm" onClick={clearAll} className="border-zinc-700 text-[10px] h-7">{language === 'IT' ? 'Nessuna' : 'Clear'}</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {availableSections.map(s => {
                  const Ic = SECTION_ICONS[s] || FileText;
                  const active = selectedSections.includes(s);
                  return (
                    <div key={s} onClick={() => toggleSection(s)}
                      className={`p-2.5 rounded-sm border cursor-pointer transition-all ${active ? 'bg-amber-500/10 border-amber-500/50 text-amber-400' : 'bg-zinc-950/50 border-zinc-800 text-zinc-500 hover:border-zinc-700 hover:text-zinc-400'}`}
                      data-testid={`section-${s}`}
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox checked={active} className="pointer-events-none" />
                        <Ic className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="text-xs leading-tight">{SECTION_LABELS[s] || s}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Options & Filters */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-zinc-100 text-base">{language === 'IT' ? 'Opzioni' : 'Options'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { id: 'charts', checked: includeCharts, set: setIncludeCharts, label: language === 'IT' ? 'Includi grafici' : 'Include charts' },
                  { id: 'tables', checked: includeTables, set: setIncludeTables, label: language === 'IT' ? 'Includi tabelle' : 'Include tables' },
                  { id: 'compare', checked: includeComparison, set: setIncludeComparison, label: language === 'IT' ? 'Confronto periodo prec.' : 'Compare with previous period' }
                ].map(({ id, checked, set, label }) => (
                  <div key={id} className="flex items-center gap-2.5">
                    <Checkbox id={id} checked={checked} onCheckedChange={set} />
                    <Label htmlFor={id} className="text-zinc-300 text-sm cursor-pointer">{label}</Label>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
                  <Filter className="w-4 h-4 text-zinc-500" />{language === 'IT' ? 'Filtri' : 'Filters'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs">{language === 'IT' ? 'Tipo Evento' : 'Event Type'}</Label>
                  <Select value={eventTypeFilter || "all"} onValueChange={v => setEventTypeFilter(v === "all" ? null : v)}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="all">{language === 'IT' ? 'Tutti' : 'All'}</SelectItem>
                      {config.event_types.map(et => <SelectItem key={et} value={et}>{et.replace('_', ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {reportType === 'TEAM' && (
                  <div className="space-y-1.5">
                    <Label className="text-zinc-400 text-xs">{language === 'IT' ? 'Minuti Minimi' : 'Min Minutes (matches)'}</Label>
                    <Select value={minMinutesFilter || "all"} onValueChange={v => setMinMinutesFilter(v === "all" ? null : v)}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="all">{language === 'IT' ? 'Tutti' : 'All players'}</SelectItem>
                        <SelectItem value="60">60+ min</SelectItem>
                        <SelectItem value="45">45+ min</SelectItem>
                        <SelectItem value="30">30+ min</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Generate */}
          <div className="flex justify-end">
            <Button onClick={handleGenerate}
              disabled={loading || (reportType === 'TEAM' && !selectedTeam) || (reportType === 'ATHLETE' && !selectedAthlete)}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold px-8"
              data-testid="generate-report-btn"
            >
              {loading ? <Clock className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              {language === 'IT' ? 'Genera Report' : 'Generate Report'}
            </Button>
          </div>
        </TabsContent>

        {/* ═══ VIEW / DASHBOARD (shows when report is generated from advanced) ═══ */}
        <TabsContent value="view" className="mt-4">
          {report && (
            <div className="space-y-4">
              {/* Report Header Bar */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-zinc-900/50 border border-zinc-800 rounded-sm" data-testid="report-header">
                <div>
                  <h2 className="text-lg font-bold text-zinc-100">{report.team?.name || report.athlete?.name}</h2>
                  <p className="text-xs text-zinc-500">
                    {PERIOD_LABELS[report.period.type]} &middot; {report.period.from} &rarr; {report.period.to}
                    {report.athlete?.position && <span> &middot; {report.athlete.position}</span>}
                    {report.athlete?.status && <span> &middot; {report.athlete.status}</span>}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setActiveTab('builder')} className="border-zinc-700 h-9 text-xs" data-testid="edit-report-btn">
                    <Settings className="w-3.5 h-3.5 mr-1.5" />{language === 'IT' ? 'Modifica' : 'Edit'}
                  </Button>
                  <Button onClick={handleExportPDF} className="bg-zinc-100 text-zinc-900 hover:bg-white h-9 text-xs font-bold" data-testid="export-pdf-btn">
                    <Printer className="w-3.5 h-3.5 mr-1.5" />{language === 'IT' ? 'Esporta PDF' : 'Export PDF'}
                  </Button>
                </div>
              </div>

              {/* Report Content (printable area) */}
              <div ref={reportRef} data-testid="report-content">
                {report.report_type === 'TEAM' ? (
                  <>
                    <TeamLoadSection load={report.sections.load} loadPrev={report.sections.load_previous} comparison={includeComparison} charts={includeCharts} lang={language} />
                    <TeamTrainingMatchSection data={report.sections.training_vs_match} charts={includeCharts} lang={language} />
                    <TeamMatchAnalysis data={report.sections.match_analysis} lang={language} />
                    <TeamAvailability data={report.sections.availability} lang={language} />
                    <TeamInjurySection data={report.sections.injury} lang={language} />
                    <TeamRankingsSection data={report.sections.rankings} charts={includeCharts} tables={includeTables} lang={language} />
                  </>
                ) : (
                  <>
                    <AthleteLoadSection load={report.sections.load} loadPrev={report.sections.load_previous} trend={report.sections.load_trend} comparison={includeComparison} charts={includeCharts} lang={language} />
                    <AthleteWellnessSection data={report.sections.wellness} dataPrev={report.sections.wellness_previous} comparison={includeComparison} charts={includeCharts} lang={language} />
                    <AthleteWeightSection data={report.sections.weight} charts={includeCharts} lang={language} />
                    <AthleteRTPSection data={report.sections.rtp} lang={language} />
                    <AthleteAttendanceSection data={report.sections.attendance} lang={language} />
                  </>
                )}

                {/* Timestamp */}
                <div className="text-[10px] text-zinc-600 text-right mt-6 print:text-gray-400">
                  {language === 'IT' ? 'Generato il' : 'Generated on'}: {new Date(report.generated_at).toLocaleString()}
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StaffReportsPage;
